import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { makeStyles } from "@mui/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import swal from "sweetalert";
import AppHeader from "../../../Layout/AppHeader";
import AppSidebar from "../../../Layout/AppSidebar";
import MsgDataService from "../../../services/MsgService";
import SearchIcon from "../../../assets/images/dashboardCounselor/search.png";
import MessagesApprovalDetailsModal from "./MessagesApprovalDetailsModal";
import MessagesApprovalFilterDrawer, {
  EMPTY_APPROVAL_FILTERS,
  hasActiveApprovalFilters,
} from "./MessagesApprovalFilterDrawer";
import {
  getRequestStatusKey,
  getRequestStatusLabel,
  normalizeApprovalStatus,
} from "../../../utils/approvalStatus";
import {
  APPROVAL_PAGE_SIZE,
  parseApprovalListResponse,
} from "../../../utils/approvalListResponse";
import "./MessagesApproval.css";

const SEARCH_DEBOUNCE_MS = 500;

const useStyles = makeStyles({
  root: {
    "& .super-app-theme--header": {
      fontSize: "13px",
    },
  },
});

const formatRequestDate = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM DD, hh:mm A") : value;
};

const parseCost = (value) => {
  if (value == null || value === "") return 0;
  const num = parseFloat(String(value).replace(/[$,]/g, ""));
  return Number.isNaN(num) ? 0 : num;
};

const formatCost = (value) => `$${value.toFixed(2)}`;

const capitalize = (value) => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const getActionLabel = (item) => {
  if (item?.action) return item.action;
  if (item?.message_action) return item.message_action;
  return "Message Request";
};

const getMessageTypeLabel = (item) => {
  if (item?.delivery_type) return item.delivery_type;
  if (item?.message_type) return item.message_type;
  return "—";
};

const formatRequester = (requester) => {
  if (!requester?.name) return "—";
  const roleLabel = requester.role ? `${capitalize(requester.role)} ` : "";
  return `${roleLabel}${requester.name}`.trim();
};

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "Something went wrong. Please try again.";
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.join(", ");
  return (
    data.message ||
    data.error_message ||
    data.error ||
    data.detail ||
    "Something went wrong. Please try again."
  );
};

const showApiError = (error) => {
  swal({
    text: getApiErrorMessage(error),
    icon: "warning",
    dangerMode: true,
    buttons: { confirm: true },
    closeOnClickOutside: false,
  });
};

const showSuccess = (message) => {
  swal({
    title: "Success!",
    text: message,
    icon: "success",
    buttons: { OK: true },
    closeOnClickOutside: false,
  });
};

const cellText = (value, color = "#374151", fontWeight = "500") => (
  <div
    style={{
      fontSize: "12px",
      color,
      fontWeight,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
    title={value || ""}
  >
    {value || "—"}
  </div>
);

const MessagesApproval = () => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionRowId, setActionRowId] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [gridPageSize, setGridPageSize] = useState(APPROVAL_PAGE_SIZE);
  const gridPageSizeRef = useRef(APPROVAL_PAGE_SIZE);
  const [summary, setSummary] = useState({
    totalCostCurrentMonth: 0,
    pendingApprovalCost: 0,
    approvedApprovalCost: 0,
    declinedApprovalCost: 0,
  });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_APPROVAL_FILTERS });
  const [draftFilters, setDraftFilters] = useState({ ...EMPTY_APPROVAL_FILTERS });

  const filtersActive = hasActiveApprovalFilters(appliedFilters);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(searchText.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchApprovals = useCallback(() => {
    setLoading(true);
    MsgDataService.getMessageApprovals(page, debouncedQ, appliedFilters)
      .then((res) => {
        const { payload, items, rowCount, serverPageSize } = parseApprovalListResponse(
          res,
          { knownPageSize: gridPageSizeRef.current, currentPage: page }
        );
        if (serverPageSize > 0 && serverPageSize !== gridPageSizeRef.current) {
          gridPageSizeRef.current = serverPageSize;
          setGridPageSize(serverPageSize);
        }
        const mappedRows = items.map((item) => ({
          id: item.id,
          requestDate: formatRequestDate(item.request_initiate_time),
          subjectTarget: item.message_body || "—",
          requester: formatRequester(item.requester),
          requestStatus: item.request_status,
          statusKey: getRequestStatusKey(item),
          actionLabel: getActionLabel(item),
          messageType: getMessageTypeLabel(item),
          college: item.requester?.college_name || "—",
          district: item.requester?.district_name || "—",
          estimatedCost: item.estimated_cost || "$0.00",
          raw: item,
        }));
        setRows(mappedRows);

        setSummary({
          totalCostCurrentMonth: parseCost(payload.total_message_cost ?? 0),
          pendingApprovalCost: parseCost(payload.pending_message_cost ?? 0),
          approvedApprovalCost: parseCost(payload.approved_message_cost ?? 0),
          declinedApprovalCost: parseCost(payload.decline_message_cost ?? 0),
        });

        setTotalCount(rowCount);
      })
      .catch(() => {
        setRows([]);
        setTotalCount(0);
        setSummary({
          totalCostCurrentMonth: 0,
          pendingApprovalCost: 0,
          approvedApprovalCost: 0,
          declinedApprovalCost: 0,
        });
      })
      .finally(() => setLoading(false));
  }, [page, debouncedQ, appliedFilters]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handlePageChange = (newPage) => {
    setPage(newPage + 1);
  };

  const openFilterDrawer = () => {
    setDraftFilters({ ...appliedFilters });
    setFilterDrawerOpen(true);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setPage(1);
    setFilterDrawerOpen(false);
  };

  const handleResetFilters = () => {
    const empty = { ...EMPTY_APPROVAL_FILTERS };
    setDraftFilters(empty);
    if (!filtersActive) {
      setFilterDrawerOpen(false);
      return;
    }
    setAppliedFilters(empty);
    setPage(1);
    setFilterDrawerOpen(false);
  };

  const handleViewDetails = useCallback(async (row) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setSelectedDetails(null);
    try {
      const response = await MsgDataService.getMessageApprovalById(row.id);
      setSelectedDetails(response?.data?.object || row.raw || null);
    } catch (error) {
      setDetailsOpen(false);
      showApiError(error);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const handleStatusUpdate = useCallback(
    async (row, requestStatus) => {
      const isApprove = requestStatus === "approved";
      const confirmed = await swal({
        title: isApprove ? "Approve Message?" : "Decline Message?",
        text: isApprove
          ? "This will approve the message request."
          : "This will decline the message request.",
        icon: "warning",
        buttons: {
          cancel: "Cancel",
          confirm: isApprove ? "Approve" : "Decline",
        },
        dangerMode: !isApprove,
        closeOnClickOutside: false,
      });
      if (!confirmed) return;

      setActionRowId(row.id);
      try {
        const response = await MsgDataService.updateMessageApprovalStatus(
          row.id,
          requestStatus
        );
        showSuccess(
          response?.data?.message ||
            `Message request ${isApprove ? "approved" : "declined"} successfully.`
        );
        fetchApprovals();
      } catch (error) {
        showApiError(error);
      } finally {
        setActionRowId(null);
      }
    },
    [fetchApprovals]
  );

  const columns = useMemo(
    () => [
      {
        field: "requestDate",
        headerName: "Request Date",
        headerClassName: "super-app-theme--header",
        flex: 1,
        minWidth: 130,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => cellText(params.value, "#6b7280"),
      },
      {
        field: "subjectTarget",
        headerName: "Message Body",
        headerClassName: "super-app-theme--header",
        flex: 1.2,
        minWidth: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => cellText(params.value, "#2563eb", "600"),
      },
      {
        field: "requester",
        headerName: "Requester",
        headerClassName: "super-app-theme--header",
        flex: 1.1,
        minWidth: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => cellText(params.value, "#059669", "600"),
      },
      {
        field: "requestStatus",
        headerName: "Request Status",
        headerClassName: "super-app-theme--header",
        flex: 1,
        minWidth: 130,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <span
            className={`messages-approval-status ${params.row.statusKey || normalizeApprovalStatus(params.value)}`}
          >
            {getRequestStatusLabel(params.row.statusKey || params.value)}
          </span>
        ),
      },
      // {
      //   field: "actionLabel",
      //   headerName: "Action",
      //   headerClassName: "super-app-theme--header",
      //   flex: 1,
      //   minWidth: 120,
      //   sortable: false,
      //   disableColumnMenu: true,
      //   renderCell: (params) => cellText(params.value),
      // },
      {
        field: "messageType",
        headerName: "Message Type",
        headerClassName: "super-app-theme--header",
        flex: 1,
        minWidth: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => cellText(params.value),
      },
      {
        field: "college",
        headerName: "College",
        headerClassName: "super-app-theme--header",
        flex: 1.1,
        minWidth: 140,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => cellText(params.value),
      },
      {
        field: "district",
        headerName: "District",
        headerClassName: "super-app-theme--header",
        flex: 1.1,
        minWidth: 140,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => cellText(params.value),
      },
      // {
      //   field: "statusLabel",
      //   headerName: "Status",
      //   headerClassName: "super-app-theme--header",
      //   flex: 1,
      //   minWidth: 130,
      //   sortable: false,
      //   disableColumnMenu: true,
      //   renderCell: (params) => (
      //     <span
      //       className={`messages-approval-status-text ${normalizeStatus(
      //         params.row.requestStatus
      //       )}`}
      //     >
      //       {params.value}
      //     </span>
      //   ),
      // },
      {
        field: "estimatedCost",
        headerName: "Est. Cost",
        headerClassName: "super-app-theme--header",
        flex: 0.8,
        minWidth: 90,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => cellText(params.value, "#111827", "700"),
      },
      {
        field: "adminAction",
        headerName: "Admin Action",
        headerClassName: "super-app-theme--header",
        flex: 1.1,
        minWidth: 130,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const status = params.row.statusKey || normalizeApprovalStatus(params.row.requestStatus);
          const isBusy = actionRowId === params.row.id;
          const isFinalStatus =
            status === "approved" ||
            status === "sent" ||
            status === "declined" ||
            status === "decline";

          if (isFinalStatus) {
            return (
              <div className="messages-approval-admin-actions">
                <button
                  type="button"
                  className="messages-approval-admin-btn view"
                  disabled={isBusy}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails(params.row);
                  }}
                >
                  View Details
                </button>
              </div>
            );
          }

          return (
            <div className="messages-approval-admin-actions">
              <button
                type="button"
                className="messages-approval-admin-btn approve"
                disabled={isBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusUpdate(params.row, "approved");
                }}
              >
                Approve
              </button>
              <button
                type="button"
                className="messages-approval-admin-btn decline"
                disabled={isBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusUpdate(params.row, "decline");
                }}
              >
                Decline
              </button>
              <button
                type="button"
                className="messages-approval-admin-btn view"
                disabled={isBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDetails(params.row);
                }}
              >
                View Details
              </button>
            </div>
          );
        },
      },
    ],
    [actionRowId, handleStatusUpdate, handleViewDetails]
  );

  const gridSx = useMemo(
    () => ({
      border: "none",
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: "#f8fafc",
        borderBottom: "2px solid #e2e8f0",
        fontSize: "13px",
        fontWeight: "700",
        color: "#1f2937",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "700",
      },
      "& .MuiDataGrid-cell": {
        borderBottom: "1px solid #f1f5f9",
        fontSize: "13px",
        padding: "0 12px",
        display: "flex",
        alignItems: "center",
      },
      "& .MuiDataGrid-row": {
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: "#f0f9ff",
        },
      },
      "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: "#fafafa",
      },
      "& .MuiDataGrid-footerContainer": {
        borderTop: "2px solid #e2e8f0",
        backgroundColor: "#f8fafc",
        minHeight: "56px",
        padding: "0 20px",
        overflow: "hidden",
      },
      "& .MuiTablePagination-root": {
        overflow: "hidden",
      },
      "& .MuiTablePagination-displayedRows": {
        fontSize: "14px",
        fontWeight: "600",
        color: "#374151",
        margin: 0,
      },
      "& .MuiTablePagination-selectLabel": {
        display: "none",
      },
      "& .MuiTablePagination-select": {
        display: "none",
      },
      "& .MuiTablePagination-spacer": {
        flex: "none",
      },
      "& .MuiTablePagination-toolbar": {
        minHeight: "56px",
        paddingLeft: "0",
        paddingRight: "0",
        overflow: "hidden",
      },
      "& .MuiTablePagination-actions": {
        marginLeft: "20px",
        overflow: "hidden",
        "& .MuiIconButton-root": {
          backgroundColor: "#3b82f6",
          color: "white",
          borderRadius: "8px",
          padding: "6px",
          marginLeft: "8px",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: "#2563eb",
          },
          "&.Mui-disabled": {
            backgroundColor: "#e5e7eb",
            color: "#9ca3af",
          },
        },
      },
      "& .MuiDataGrid-virtualScroller": {
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f5f9",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#cbd5e1",
          borderRadius: "4px",
          "&:hover": {
            background: "#94a3b8",
          },
        },
      },
      "& .MuiDataGrid-cell:focus": {
        outline: "none !important",
      },
    }),
    []
  );

  return (
    <Fragment>
      <AppHeader />
      <div className="app-main">
        <AppSidebar />
        <div className="app-main__outer">
          <div className="csv-import-container">
            <div className="messages-approval-header-card">
              <div className="messages-approval-header-top-row">
                <h1 className="messages-approval-page__title">Nudges Approval</h1>
                <div className="messages-approval-header-actions">
                  <div className="messages-approval-search">
                    <input
                      type="text"
                      placeholder="Search by Requester"
                      value={searchText}
                      autoComplete="off"
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <img
                      src={SearchIcon}
                      alt="Search"
                      className="messages-approval-search-icon"
                    />
                  </div>
                  <div className="messages-approval-filter-btn-wrap">
                    <button
                      type="button"
                      className="messages-approval-filter-icon-btn"
                      aria-label="Open filters"
                      onClick={openFilterDrawer}
                    >
                      <FontAwesomeIcon icon={faFilter} />
                    </button>
                    {filtersActive && <span className="messages-approval-filter-dot" />}
                  </div>
                </div>
              </div>

              <div className="messages-approval-summary-row">
                <div className="messages-approval-summary-badge sent-cost">
                  <span className="messages-approval-summary-badge__label">
                    Sent Messages Cost
                  </span>
                  <span className="messages-approval-summary-badge__value">
                    {formatCost(summary.totalCostCurrentMonth)}
                  </span>
                </div>
                <div className="messages-approval-summary-badge pending-cost">
                  <span className="messages-approval-summary-badge__label">
                    Pending for Approval Messages Cost
                  </span>
                  <span className="messages-approval-summary-badge__value">
                    {formatCost(summary.pendingApprovalCost)}
                  </span>
                </div>
                <div className="messages-approval-summary-badge approved-cost">
                  <span className="messages-approval-summary-badge__label">
                    Approved Messages Cost
                  </span>
                  <span className="messages-approval-summary-badge__value">
                    {formatCost(summary.approvedApprovalCost)}
                  </span>
                </div>
                <div className="messages-approval-summary-badge decline-cost">
                  <span className="messages-approval-summary-badge__label">
                    Declined Messages Cost
                  </span>
                  <span className="messages-approval-summary-badge__value">
                    {formatCost(summary.declinedApprovalCost)}
                  </span>
                </div>
              </div>
            </div>

            <div className="messages-approval-table-wrap">
              <div className={`messages-approval-table-panel ${classes.root}`}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSize={gridPageSize}
                  rowsPerPageOptions={[gridPageSize]}
                  pagination
                  paginationMode="server"
                  rowCount={totalCount}
                  page={page - 1}
                  onPageChange={handlePageChange}
                  loading={loading}
                  disableSelectionOnClick
                  disableColumnMenu
                  hideFooterSelectedRowCount
                  rowHeight={96}
                  headerHeight={48}
                  sx={gridSx}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <MessagesApprovalDetailsModal
        isOpen={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedDetails(null);
        }}
        loading={detailsLoading}
        details={selectedDetails}
      />

      <MessagesApprovalFilterDrawer
        isOpen={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        draftFilters={draftFilters}
        onDraftChange={setDraftFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </Fragment>
  );
};

export default MessagesApproval;
